import { prospectArrayFields } from "../constants";

/**
 * Helper functions for data transformation and type inference
 */
export const inferAjvType = (value) => {
  if (value === null || value === undefined || value === "") {
    return { type: "string" };
  }

  const strValue = String(value).trim();

  // Check for boolean
  if (strValue.toLowerCase() === "true" || strValue.toLowerCase() === "false") {
    return { type: "boolean" };
  }

  // Check for integer
  if (/^-?\d+$/.test(strValue)) {
    return { type: "integer" };
  }

  // Check for number (including decimals)
  if (/^-?\d*\.?\d+$/.test(strValue)) {
    return { type: "number" };
  }

  // Check for date patterns
  if (/^\d{4}-\d{2}-\d{2}/.test(strValue)) {
    return { type: "string", format: "date" };
  }

  // Check for email
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(strValue)) {
    return { type: "string", format: "email" };
  }

  // Check for URL
  if (/^https?:\/\//.test(strValue)) {
    return { type: "string", format: "url" };
  }

  // Default to string
  return { type: "string" };
};

/**
 * Initialize AJV schemas based on inferred types from file data
 */
export const initializeAjvSchemas = (fileData) => {
  const properties = {};
  const fieldKeys = [];
  Object.entries(fileData).forEach(([key, value]) => {
    properties[key] = inferAjvType(value);
    fieldKeys.push(key);
  });

  const newSchema = {
    type: "object",
    required: fieldKeys, // Set all fields as required by default
    properties,
  };

  return {
    default: newSchema,
  };
};

/**
 * Initialize ingestion mappings with source attributes
 */
export const initializeIngestionMappings = (sourceAttributes, formValues) => {
  const mappings = {};

  // Add mappings for source attributes
  sourceAttributes.forEach((attr) => {
    let targetField = attr;
    const include = "() => true";
    const includeFunction = "(e) => e";

    // Smart mapping based on attribute names
    if (attr.toLowerCase().includes("date")) {
      if (attr.toLowerCase().includes("birth")) {
        targetField = "dateOfBirth";
      } else if (attr.toLowerCase().includes("created")) {
        targetField = "createdAt";
      } else if (attr.toLowerCase().includes("updated")) {
        targetField = "updatedAt";
      }
    } else if (attr.toLowerCase().includes("country")) {
      targetField = "country";
    } else if (attr.toLowerCase().includes("email")) {
      targetField = "email";
    } else if (attr.toLowerCase().includes("phone")) {
      targetField = "phone";
    } else if (attr.toLowerCase().includes("first") && attr.toLowerCase().includes("name")) {
      targetField = "firstName";
    } else if (attr.toLowerCase().includes("last") && attr.toLowerCase().includes("name")) {
      targetField = "lastName";
    }

    mappings[attr] = {
      sourceField: attr,
      targetField,
      include,
      includeFunction,
      isFixed: false,
    };
  });

  // Add default mappings for audit/timestamp fields
  const defaultMappings = {
    createdAt: {
      sourceField: "createdAt",
      targetField: "createdAt",
      include: "() => true",
      includeFunction: "() => true",
      modifierFunction: "() => new Date().toISOString()",
      isFixed: false,
      isDefault: true,
    },
    updatedAt: {
      sourceField: "updatedAt",
      targetField: "updatedAt",
      include: "() => true",
      includeFunction: "() => true",
      modifierFunction: "() => new Date().toISOString()",
      isFixed: false,
      isDefault: true,
    },
    createdBy: {
      sourceField: "createdBy",
      targetField: "createdBy",
      include: "() => true",
      includeFunction: "() => true",
      modifierFunction: `() => "${formValues.partnerName || "default_partner"}"`,
      isFixed: false,
      isDefault: true,
    },
    updatedBy: {
      sourceField: "updatedBy",
      targetField: "updatedBy",
      include: "() => true",
      includeFunction: "() => true",
      modifierFunction: `() => "${formValues.partnerName || "default_partner"}"`,
      isFixed: false,
      isDefault: true,
    },
  };

  // Check if source attributes contain these fields, if not add defaults
  Object.entries(defaultMappings).forEach(([key, defaultMapping]) => {
    const existingField = sourceAttributes.find((attr) => attr.toLowerCase() === key.toLowerCase());

    if (existingField) {
      // If field exists in source, use it with proper include function
      mappings[existingField] = {
        sourceField: existingField,
        targetField: key,
        include: "() => true",
        includeFunction: "(e) => e",
        isFixed: false,
      };
    } else {
      // If field doesn't exist, add default mapping (no path since not in payload)
      mappings[key] = defaultMapping;
    }
  });

  return mappings;
};

/**
 * Generate array object templates for drag and drop mappings
 */
export const getArrayObjectTemplate = (arrayField, sourceField, partnerName) => {
  const templates = {
    emailAddresses: {
      path: sourceField,
      include: "(e) => e",
      modifier: `(val, src) => [{ emailAddress: val, createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }]`,
    },
    phoneNumbers: {
      path: sourceField,
      include: "(e) => e",
      modifier: `(val, src) => [{ phoneNumber: val, createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }]`,
    },
    consents: {
      path: sourceField,
      include: "(e) => e",
      modifier: `(val, src) => [{ createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", consentType: "${sourceField}", granted: val === true ? 1 : 0 }]`,
    },
    commPreferences: {
      path: sourceField,
      include: "(e) => e",
      modifier: `(val, src) => [{ commPreferenceType: "${partnerName}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", channel: val, granted: 1 }]`,
    },
    enrichmentData: {
      path: sourceField,
      include: "(e) => e",
      modifier: `(val, src) => [{ enrichmentValue: val, category: "default", enrichmentType: "custom", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }]`,
    },
    legalAcceptances: {
      path: sourceField,
      include: "(e) => e",
      modifier: `(val, src) => [{ legalAcceptanceType: val || "terms", granted: val === "true" ? 1 : 0, createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }]`,
    },
  };
  return templates[arrayField] || { path: sourceField, include: "(e) => e" };
};

/**
 * Build complete configuration object
 */
export const buildCompleteConfig = (
  formValues,
  ageRanges,
  ajvSchemas,
  mappers,
  user,
  filePreview,
  fileMetadata,
  selectedRange,
  selectedSchemaMarket,
  selectedAttribute,
  ingestionMappings,
  prospectMappings,
  enrichConsumerMappings,
  arrayDefaults,
  availableIngestionFields,
  availableIngestionFieldsEnrich
) => {
  const userName = sessionStorage.getItem("userName");

  // Helper to ensure array fields are present in a mapper
  const ensureArrayFields = (mapper, arrayFields) => {
    const updated = { ...mapper };
    arrayFields.forEach((field) => {
      if (!updated[field]) {
        updated[field] = {};
      }
    });
    return updated;
  };

  // Helper to add default audit fields with include/modifier functions
  const addDefaultFields = (mapper) => {
    return {
      ...mapper,
      createdAt: {
        include: "() => true",
        modifier: "() => new Date().toISOString()",
      },
      updatedAt: {
        include: "() => true",
        modifier: "() => new Date().toISOString()",
      },
      createdBy: {
        include: "() => true",
        modifier: `() => "${formValues.partnerName || "PARTNER_NAME"}"`,
      },
      updatedBy: {
        include: "() => true",
        modifier: `() => "${formValues.partnerName || "PARTNER_NAME"}"`,
      },
    };
  };

  // Helper to clean up mapper by removing undefined/null values
  const cleanMapper = (mapper) => {
    const cleaned = {};
    Object.entries(mapper).forEach(([key, value]) => {
      if (value && (typeof value === "object" ? Object.keys(value).length > 0 : true)) {
        cleaned[key] = value;
      }
    });
    return cleaned;
  };

  const ingestionMapperObj = cleanMapper(mappers.ingestion || {});
  const createProspectMapperObj = cleanMapper(
    addDefaultFields(ensureArrayFields(mappers.createProspect || {}, prospectArrayFields))
  );
  const enrichConsumerMapperObj = cleanMapper(
    addDefaultFields(ensureArrayFields(mappers.enrichConsumer || {}, prospectArrayFields))
  );

  // Ensure enrichConsumer has a default mapping for kocid -> kocid using minimal string form
  if (!enrichConsumerMapperObj.kocid) {
    enrichConsumerMapperObj.kocid = "kocid";
  }

  return {
    partnerName: formValues.partnerName || "PARTNER_NAME",
    countryHeader: formValues.countryAttribute || "country",
    dobHeader: formValues.dobAttribute || "dateOfBirth",
    hashIdentifier: formValues.hashIdentifier || "email",
    ageRanges,
    ajvSchemas,
    mappers: {
      ingestion: JSON.stringify(ingestionMapperObj),
      createProspect: JSON.stringify(createProspectMapperObj),
      enrichConsumer: JSON.stringify(enrichConsumerMapperObj),
    },
    platform: "v2",
    userName,
    region: user?.region,
    // Save UI state for reconstruction
    uiState: {
      filePreview,
      fileMetadata,
      selectedRange,
      selectedSchemaMarket,
      selectedAttribute,
      formValues,
      ingestionMappings,
      prospectMappings,
      enrichConsumerMappings,
      arrayDefaults,
      availableIngestionFields,
      availableIngestionFieldsEnrich,
    },
  };
};

/**
 * Helper functions for proper mapper generation
 */
export const always = () => true;
export const identity = (val) => val;

export const getValue = (obj, path, defaultValue = null) => {
  if (path) {
    const input = Array.isArray(path) ? path : path.split(".");
    const value = input.reduce((res, prop) => res?.[prop], obj);
    return value || defaultValue;
  }
  return defaultValue;
};

export const getMapper = (schema) => {
  return (source) => {
    // Guard against null/undefined source
    if (!source || typeof source !== "object") {
      return {};
    }

    return Object.keys(schema).reduce((result, key) => {
      const entry = schema[key];

      const isPath = typeof entry === "string" || Array.isArray(entry);

      const include = isPath ? always : entry.include || always;
      const path = isPath ? entry : entry.path;

      const value = getValue(source, path);

      if (!include(value, source)) return result;

      const modifier = isPath ? identity : entry.modifier || identity;

      return { ...result, [key]: modifier(value, source) };
    }, {});
  };
};

export const jsonSafeParse = (obj, reviver) => {
  if (typeof obj === "string") {
    return JSON.parse(obj, reviver);
  }
  // If it's already an object, apply the reviver function to each property
  const result = {};
  Object.keys(obj).forEach((key) => {
    result[key] = reviver ? reviver(key, obj[key]) : obj[key];
  });
  return result;
};

export const createMapper = (config) => {
  try {
    if (typeof config === "function") {
      // If already a function, return it
      return config;
    }

    if (typeof config !== "string") {
      // For object config, create mapper using the schema pattern
      return getMapper(config);
    }

    // Parse the string config and create mapper
    const configObj = jsonSafeParse(config, (key, value) => {
      if (typeof value === "string" && value.startsWith("function")) {
        // Use eval for function strings - this is the original working approach
        try {
          // eslint-disable-next-line no-eval
          return eval(`(${value})`);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn(`Failed to parse function for key ${key}:`, error);
          return value;
        }
      }
      return value;
    });

    return getMapper(configObj);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error creating mapper:", error);
    return getMapper({});
  }
};

export const createMappers = (config) => {
  const { mappers } = config;
  return Object.keys(mappers).reduce((acc, curr) => {
    const rawMapperConfig = mappers[curr];

    // Handle both stringified and object mappers for backward compatibility
    const mapperConfig =
      typeof rawMapperConfig === "string" ? JSON.parse(rawMapperConfig) : rawMapperConfig;

    const parsedMapperConfig = jsonSafeParse(mapperConfig, (_, val) => {
      if (val && typeof val === "object" && val.modifier) {
        // eslint-disable-next-line no-eval, no-param-reassign
        val.modifier = eval(val.modifier);
      }
      if (val && typeof val === "object" && val.include) {
        // eslint-disable-next-line no-eval, no-param-reassign
        val.include = eval(val.include);
      }

      return val;
    });
    // eslint-disable-next-line no-param-reassign
    acc[curr] = getMapper(parsedMapperConfig);
    return acc;
  }, {});
};
